import { Box, Button, Container, Grid, IconButton } from "@mui/material";
import React, { useState } from "react";
import { useQuery } from "react-query";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Iconify from "src/components/Iconify";
import Page from "src/components/Page";
import MRTTable from "src/components/mrt-table";
import useSettings from "src/hooks/useSettings";
import useAuth from "src/hooks/useAuth";
import { getAllDesignations } from "src/pages/api";
import AddDesignation from "./AddDesignation";
import DeleteRecord from "src/components/DeleteRecord";

export default function DesignationMaster() {
  const { themeStretch } = useSettings();
  const { user } = useAuth();
  const isCompanyUser = user?.role === "COMPANY";

  const columns = [
    ...(!isCompanyUser
      ? [{ accessorFn: (row) => row.company?.name || row.companyName || "-", id: "companyName", header: "Company", size: 250 }]
      : []),
    { accessorKey: "name", header: "Name", size: 250 },
  ];

  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["designations", pagination.pageIndex, pagination.pageSize, globalFilter || ""],
    queryFn: getAllDesignations,
    keepPreviousData: true,
    staleTime: 0,
  });

  const handleRowClick = (row) => {
    setOpen(true);
    setSelectedRow(row.original);
    setIsEditMode(true);
  };

  return (
    <>
      {open && (
        <AddDesignation
          onClose={() => {
            setOpen(false);
            setSelectedRow(null);
            setIsEditMode(false);
            refetch();
          }}
          row={selectedRow}
          isEditMode={isEditMode}
        />
      )}
      {openDelete && selectedRow && (
        <DeleteRecord
          row={selectedRow}
          title="Delete Designation"
          endpoint="/designations"
          labelKey="name"
          queryKey={["designations"]}
          onClose={() => {
            setOpenDelete(false);
            setSelectedRow(null);
            refetch();
          }}
        />
      )}
      <Page title="Designation">
        <Container maxWidth={themeStretch ? false : "xl"}>
          <HeaderBreadcrumbs
            heading="Designations"
            links={[
              { name: "Master" },
              { name: "Designations" },
            ]}
          />
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ overflowX: "auto", minWidth: 0 }}>
              <MRTTable
                data={data?.data || []}
                columns={columns}
                manualPagination={true}
                loading={isLoading || isFetching}
                onPaginationChange={setPagination}
                pagination={pagination}
                rowCount={data?.total || 0}
                onGlobalFilterChange={(value) => {
                  setGlobalFilter(value ?? "");
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => handleRowClick(row),
                  sx: {
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                  },
                })}
                renderRowActions={({ row }) => (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDelete(true);
                        setSelectedRow(row.original);
                      }}
                      color="error"
                      size="small"
                    >
                      <Iconify icon={"eva:trash-2-outline"} />
                    </IconButton>
                  </Box>
                )}
                renderTopToolbarCustomActions={() => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        setOpen(true);
                        setIsEditMode(false);
                      }}
                      sx={{ minWidth: "120px", fontSize: "0.875rem", py: 1 }}
                    >
                      Add New
                    </Button>
                  </Box>
                )}
                enableToolbarInternalActions={false}
                enableTopToolbar={true}
                enableBottomToolbar={true}
                enableColumnActions={false}
                enableColumnFilters={false}
                enableSorting={true}
                enableGlobalFilter={true}
                enableHiding={false}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enablePagination={true}
                positionPagination="bottom"
                enableColumnDragging={false}
                positionGlobalFilter="left"
                muiSearchTextFieldProps={{
                  placeholder: "Search",
                  size: "small",
                  variant: "outlined",
                  // sx: { minWidth: "250px" },
                  value: globalFilter,
                  onChange: (e) => setGlobalFilter(e.target.value),
                }}
                initialState={{
                  showGlobalFilter: true,
                  density: "comfortable",
                  pagination: { pageSize: 10, pageIndex: 0 },
                }}
                state={{
                  globalFilter,
                  pagination,
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Page>
    </>
  );
}
